import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { slugify } from '@/lib/utils';

const emptyProject = {
    slug: '', title: '', location: '', type_of_work: '', summary: '', description: '',
    featured_image: '', gallery: [], year: new Date().getFullYear(), related_services: [],
    status: 'published', order: 0
};

export default function AdminProjects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => { setLoading(true); api.get('/admin/projects').then(({ data }) => setProjects(data)).finally(() => setLoading(false)); };
    useEffect(load, []);

    const open = (item) => { setEditing(item ? { ...item, gallery: item.gallery || [], related_services: item.related_services || [] } : { ...emptyProject }); setDialogOpen(true); };
    const close = () => { setDialogOpen(false); setEditing(null); };

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...editing };
            if (!payload.slug) payload.slug = slugify(payload.title);
            if (editing.id) {
                await api.put(`/admin/projects/${editing.id}`, payload);
                toast.success('Projekt ažuriran');
            } else {
                await api.post('/admin/projects', payload);
                toast.success('Projekt kreiran');
            }
            close();
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo'); } finally { setSaving(false); }
    };

    const onDelete = async (id) => {
        if (!confirm('Obrisati referencu?')) return;
        try { await api.delete(`/admin/projects/${id}`); toast.success('Obrisano'); load(); } catch { toast.error('Brisanje nije uspjelo'); }
    };

    const addGalleryImage = () => setEditing({...editing, gallery: [...(editing.gallery||[]), {url: '', alt: ''}]});
    const updateGallery = (i, k, v) => { const g = [...editing.gallery]; g[i] = {...g[i], [k]: v}; setEditing({...editing, gallery: g}); };
    const removeGallery = (i) => setEditing({...editing, gallery: editing.gallery.filter((_,j) => j !== i)});

    const addBeforeAfter = () => setEditing({...editing, before_after: [...(editing.before_after||[]), {before_url: '', after_url: '', alt: ''}]});
    const updateBeforeAfter = (i, k, v) => { const b = [...editing.before_after]; b[i] = {...b[i], [k]: v}; setEditing({...editing, before_after: b}); };
    const removeBeforeAfter = (i) => setEditing({...editing, before_after: editing.before_after.filter((_,j) => j !== i)});

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-medium">Reference</h1>
                    <p className="text-sm text-white/60 mt-1">Realizirani projekti.</p>
                </div>
                <Button onClick={() => open(null)} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white"><Plus className="mr-2 h-4 w-4" />Nova referenca</Button>
            </div>

            <Card className="mt-6 bg-[#111318] border-[#232A36] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="text-white/60 text-xs uppercase tracking-wider">
                        <tr className="border-b border-[#232A36]">
                            <th className="text-left px-4 py-3">Naziv</th>
                            <th className="text-left px-4 py-3">Lokacija</th>
                            <th className="text-left px-4 py-3">Tip</th>
                            <th className="text-left px-4 py-3">Godina</th>
                            <th className="text-right px-4 py-3">Akcije</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr><td colSpan="5" className="p-8 text-center text-white/60">Učitavanje…</td></tr> :
                          projects.map((p) => (
                            <tr key={p.id} className="border-b border-[#232A36] hover:bg-[#161A21]">
                                <td className="px-4 py-3 font-medium">{p.title}</td>
                                <td className="px-4 py-3">{p.location}</td>
                                <td className="px-4 py-3 text-white/70">{p.type_of_work}</td>
                                <td className="px-4 py-3">{p.year}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="outline" onClick={() => open(p)} className="border-[#232A36] text-white hover:bg-[#232A36] mr-2"><Edit2 className="h-3.5 w-3.5" /></Button>
                                    <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl bg-[#111318] border-[#232A36] text-white max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing?.id ? 'Uredi referencu' : 'Nova referenca'}</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div><Label>Naziv</Label><Input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Slug</Label><Input value={editing.slug} onChange={(e) => setEditing({...editing, slug: slugify(e.target.value)})} className="mt-2 bg-[#0B0C0E] border-[#232A36] font-mono" /></div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div><Label>Lokacija</Label><Input value={editing.location} onChange={(e) => setEditing({...editing, location: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Tip radova</Label><Input value={editing.type_of_work} onChange={(e) => setEditing({...editing, type_of_work: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                                <div><Label>Godina</Label><Input type="number" value={editing.year || ''} onChange={(e) => setEditing({...editing, year: parseInt(e.target.value) || null})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            </div>
                            <div><Label>Kratak sažetak</Label><Textarea value={editing.summary} onChange={(e) => setEditing({...editing, summary: e.target.value})} rows={2} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            <div><Label>Detaljan opis</Label><Textarea value={editing.description} onChange={(e) => setEditing({...editing, description: e.target.value})} rows={5} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            <div><Label>Featured slika (URL)</Label><Input value={editing.featured_image} onChange={(e) => setEditing({...editing, featured_image: e.target.value})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label>Galerija slika</Label>
                                    <Button size="sm" variant="outline" onClick={addGalleryImage} className="border-[#232A36] text-white hover:bg-[#232A36]"><Plus className="mr-1 h-3.5 w-3.5" />Dodaj</Button>
                                </div>
                                <div className="space-y-2">
                                    {(editing.gallery || []).map((g, i) => (
                                        <div key={i} className="flex gap-2">
                                            <Input value={g.url} onChange={(e) => updateGallery(i, 'url', e.target.value)} placeholder="URL slike" className="bg-[#0B0C0E] border-[#232A36]" />
                                            <Input value={g.alt || ''} onChange={(e) => updateGallery(i, 'alt', e.target.value)} placeholder="Alt tekst" className="bg-[#0B0C0E] border-[#232A36]" />
                                            <Button size="icon" variant="outline" onClick={() => removeGallery(i)} className="border-red-900 text-red-400"><X className="h-3.5 w-3.5" /></Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <Label>Prije / poslije slike</Label>
                                    <Button size="sm" variant="outline" onClick={addBeforeAfter} className="border-[#232A36] text-white hover:bg-[#232A36]" data-testid="project-before-after-add"><Plus className="mr-1 h-3.5 w-3.5" />Dodaj</Button>
                                </div>
                                <div className="space-y-2">
                                    {(editing.before_after || []).map((b, i) => (
                                        <div key={i} className="rounded-md border border-[#232A36] p-2 bg-[#0B0C0E] space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">#{i + 1}</span>
                                                <Button size="icon" variant="outline" onClick={() => removeBeforeAfter(i)} className="border-red-900 text-red-400 h-7 w-7" data-testid="project-before-after-remove"><X className="h-3.5 w-3.5" /></Button>
                                            </div>
                                            <Input value={b.before_url || ''} onChange={(e) => updateBeforeAfter(i, 'before_url', e.target.value)} placeholder="URL slike PRIJE *" className="bg-[#0B0C0E] border-[#232A36]" />
                                            <Input value={b.after_url || ''} onChange={(e) => updateBeforeAfter(i, 'after_url', e.target.value)} placeholder="URL slike POSLIJE *" className="bg-[#0B0C0E] border-[#232A36]" />
                                            <Input value={b.alt || ''} onChange={(e) => updateBeforeAfter(i, 'alt', e.target.value)} placeholder="Alt tekst" className="bg-[#0B0C0E] border-[#232A36]" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div><Label>Povezane usluge (slugovi, zarezom)</Label><Input value={(editing.related_services || []).join(', ')} onChange={(e) => setEditing({...editing, related_services: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="mt-2 bg-[#0B0C0E] border-[#232A36]" /></div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} className="border-[#232A36] text-white hover:bg-[#232A36]">Odustani</Button>
                        <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Spremi'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
